/**
 * Projects list page. Real data via TanStack Query.
 *
 * When the viewer is a PM, the list auto-filters to their own requests
 * (pm_user_id=me) so they see only what they've submitted.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  ClipboardCheck,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import type { ProjectListQuery, ProjectSortField, ProjectStatus, Project } from '@vari-crew/schemas';
import { ProjectStatusSchema } from '@vari-crew/schemas';
import { useDeletedProjects, useProjects, useRestoreProject } from '@/api/projects';
import { usePendingTasks } from '@/api/tasks';
import { prefetchProject } from '@/lib/prefetch';
import { SavedDraftsCard } from '@/components/projects/SavedDraftsCard';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { formatDate, formatDateOnly, formatMoneyCents } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Natural first-click direction per column: dates / money / priority show the
 * biggest (newest, largest, most urgent) first; text columns read A→Z. Clicking
 * the active column flips the direction.
 */
const SORT_DEFAULT_DIR: Record<ProjectSortField, 'asc' | 'desc'> = {
  created_at: 'desc',
  requested_start_date: 'desc',
  delivery_install_budget_cents: 'desc',
  priority: 'desc',
  status: 'asc',
  title: 'asc',
  client_name: 'asc',
};

/** Human labels for the "Sort by" control — same fields the columns sort on. */
const SORT_LABELS: Record<ProjectSortField, string> = {
  requested_start_date: 'Start date',
  created_at: 'Created',
  title: 'Title',
  client_name: 'Client',
  status: 'Status',
  priority: 'Priority',
  delivery_install_budget_cents: 'Budget',
};
const SORT_OPTIONS: ProjectSortField[] = [
  'requested_start_date',
  'created_at',
  'title',
  'client_name',
  'status',
  'priority',
  'delivery_install_budget_cents',
];

export function Projects() {
  const { data: me } = useCurrentUser();
  const isPm = me?.role === 'pm';
  // Supervisors/admins confirm PM-requested tasks; surface that queue here.
  const canSeeRequests = me?.role === 'supervisor' || me?.role === 'admin';
  // By project start date, most recent first, by default.
  const [query, setQuery] = useState<ProjectListQuery>({
    limit: 50,
    offset: 0,
    sort: 'requested_start_date',
    order: 'desc',
  });
  const [search, setSearch] = useState('');
  // "Needs confirmation" filter — only the projects with open task requests.
  const [needsConfirmOnly, setNeedsConfirmOnly] = useState(false);

  // Pending task requests across all projects → count per project, so the list
  // can badge rows + filter. Accurate (unpaginated); fetched only for approvers.
  const pendingQ = usePendingTasks(canSeeRequests);
  const requestsByProject = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of pendingQ.data?.data ?? []) m.set(t.project_id, (m.get(t.project_id) ?? 0) + 1);
    return m;
  }, [pendingQ.data]);
  const totalRequests = pendingQ.data?.data.length ?? 0;
  const projectsWithRequests = requestsByProject.size;

  function toggleSort(field: ProjectSortField) {
    setQuery((q) =>
      q.sort === field
        ? { ...q, order: q.order === 'asc' ? 'desc' : 'asc', offset: 0 }
        : { ...q, sort: field, order: SORT_DEFAULT_DIR[field], offset: 0 },
    );
  }
  // The toolbar "Sort by" control (also drives the desktop column headers, and is
  // the only sort affordance on mobile). Picking a field applies that field's
  // natural direction; the arrow button flips it.
  function setSortField(field: ProjectSortField) {
    setQuery((q) => ({ ...q, sort: field, order: SORT_DEFAULT_DIR[field], offset: 0 }));
  }
  function flipOrder() {
    setQuery((q) => ({ ...q, order: q.order === 'asc' ? 'desc' : 'asc', offset: 0 }));
  }
  const sortField = query.sort ?? 'requested_start_date';
  const sortOrder = query.order ?? 'desc';

  // PMs see only their own requests by default
  const effectiveQuery: ProjectListQuery = {
    ...query,
    ...(isPm && me?.id ? { pm_user_id: query.pm_user_id ?? me.id } : {}),
    // While "needs confirmation" is active, load enough to filter the full set
    // client-side (the request set is small) rather than just the current page.
    ...(needsConfirmOnly ? { limit: 200, offset: 0 } : {}),
  };

  const { data, isLoading, error } = useProjects(effectiveQuery);
  const qc = useQueryClient();

  // Rows actually shown — narrowed to projects with open requests when filtering.
  const rows = useMemo(() => {
    const all = data?.data ?? [];
    return needsConfirmOnly ? all.filter((p) => requestsByProject.has(p.id)) : all;
  }, [data, needsConfirmOnly, requestsByProject]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">{isPm ? 'My Requests' : 'Projects'}</h1>
          <p className="text-sm text-ink-dim">
            {needsConfirmOnly
              ? `${rows.length} project${rows.length === 1 ? '' : 's'} with task requests`
              : data?.page.total
                ? `${data.page.total} total · viewing ${Math.min(query.offset + 1, data.page.total)}–${Math.min(query.offset + (data.data?.length ?? 0), data.page.total)}`
                : 'No projects yet'}
            {canSeeRequests && totalRequests > 0 && !needsConfirmOnly && (
              <span className="font-medium text-status-requested-fg">
                {' · '}
                {totalRequests} task request{totalRequests === 1 ? '' : 's'} on {projectsWithRequests}{' '}
                project{projectsWithRequests === 1 ? '' : 's'}
              </span>
            )}
          </p>
        </div>
        <Button asChild>
          <Link to="/projects/new">{isPm ? '+ New request' : '+ New project'}</Link>
        </Button>
      </header>

      <SavedDraftsCard />

      <div className="flex gap-2 flex-wrap items-center">
        <form
          className="relative flex-1 min-w-[260px]"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery({ ...query, q: search || undefined, offset: 0 });
          }}
        >
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input
            type="text"
            placeholder="Search title / client / SAP IDs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-line rounded-md bg-bg focus:outline-none focus:border-accent"
          />
        </form>
        <StatusFilter
          value={query.status}
          onChange={(s) => setQuery({ ...query, status: s, offset: 0 })}
        />
        {/* Sort control — mirrors the app's "Sort by" convention (CrewChat); also
            the only sort affordance on mobile, where the sortable table headers
            aren't shown. */}
        <div className="inline-flex items-center gap-1 rounded-md border border-line bg-bg pl-2.5 pr-1 py-1 text-xs">
          <ArrowDownUp className="w-3.5 h-3.5 text-ink-faint shrink-0" />
          <span className="sr-only">Sort by</span>
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value as ProjectSortField)}
            aria-label="Sort by"
            className="bg-transparent font-medium text-ink focus:outline-none cursor-pointer pr-1"
          >
            {SORT_OPTIONS.map((f) => (
              <option key={f} value={f}>
                {SORT_LABELS[f]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={flipOrder}
            aria-label={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'} — click to flip`}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            className="grid place-items-center rounded p-1 text-ink-dim hover:bg-fill-soft hover:text-ink"
          >
            {sortOrder === 'asc' ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        {canSeeRequests && totalRequests > 0 && (
          <button
            type="button"
            onClick={() => setNeedsConfirmOnly((v) => !v)}
            aria-pressed={needsConfirmOnly}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
              needsConfirmOnly
                ? 'border-status-requested-fg bg-status-requested text-status-requested-fg'
                : 'border-line bg-bg text-ink-dim hover:bg-fill-soft',
            )}
          >
            <ClipboardCheck className="w-3.5 h-3.5" />
            Needs confirmation
            <span className="inline-flex items-center justify-center rounded-full bg-status-requested-fg/15 px-1.5 text-[11px]">
              {projectsWithRequests}
            </span>
          </button>
        )}
      </div>

      {isLoading && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-ink-dim">Loading…</CardContent>
        </Card>
      )}

      {error && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-status-blocked-fg">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && data && (
        <Card>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="border-b border-line bg-fill-soft">
                <tr className="text-left text-xs uppercase tracking-wide text-ink-faint">
                  <SortHeader field="title" label="Title" query={query} onSort={toggleSort} />
                  <SortHeader field="client_name" label="Client" query={query} onSort={toggleSort} />
                  <SortHeader field="status" label="Status" query={query} onSort={toggleSort} />
                  <th className="px-4 py-2 font-medium">PM</th>
                  <SortHeader field="requested_start_date" label="Start" query={query} onSort={toggleSort} />
                  <SortHeader field="created_at" label="Created" query={query} onSort={toggleSort} />
                  <SortHeader
                    field="delivery_install_budget_cents"
                    label="Budget"
                    align="right"
                    query={query}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-ink-dim">
                      {needsConfirmOnly
                        ? 'No projects have task requests.'
                        : 'No projects match your filters.'}
                    </td>
                  </tr>
                )}
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    onPointerEnter={() => prefetchProject(qc, p.id)}
                    className="border-b border-line last:border-0 hover:bg-fill-soft"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/projects/${p.id}`}
                          onFocus={() => prefetchProject(qc, p.id)}
                          className="font-medium hover:underline"
                        >
                          {p.title}
                        </Link>
                        {requestsByProject.get(p.id) ? (
                          <RequestsChip count={requestsByProject.get(p.id)!} />
                        ) : null}
                      </div>
                      {p.sap_order_number && (
                        <div className="text-xs text-ink-faint font-mono">
                          {p.sap_order_number}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-dim whitespace-nowrap">{p.client_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3 text-ink-dim">{p.pm_display_name ?? '—'}</td>
                    <td className="px-4 py-3 text-ink-dim whitespace-nowrap">{formatDateOnly(p.requested_start_date)}</td>
                    <td className="px-4 py-3 text-ink-dim whitespace-nowrap">{formatDate(p.created_at)}</td>
                    <td className="px-4 py-3 text-right text-ink-dim font-mono whitespace-nowrap">
                      {formatMoneyCents(p.delivery_install_budget_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards instead of an off-screen-scrolling table. Status —
              the most-scanned field — stays visible next to the title. */}
          <ul className="md:hidden divide-y divide-line">
            {rows.length === 0 && (
              <li className="px-4 py-12 text-center text-sm text-ink-dim">
                {needsConfirmOnly
                  ? 'No projects have task requests.'
                  : 'No projects match your filters.'}
              </li>
            )}
            {rows.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/projects/${p.id}`}
                  onPointerEnter={() => prefetchProject(qc, p.id)}
                  onFocus={() => prefetchProject(qc, p.id)}
                  className="block px-4 py-3 hover:bg-fill-soft transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.title}</div>
                      <div className="text-xs text-ink-faint truncate">
                        {p.client_name}
                        {p.sap_order_number && (
                          <span className="font-mono"> · {p.sap_order_number}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <StatusBadge status={p.status} />
                      {requestsByProject.get(p.id) ? (
                        <RequestsChip count={requestsByProject.get(p.id)!} />
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-ink-dim">
                    <span className="truncate">{p.pm_display_name ?? '—'}</span>
                    <span className="text-ink-faint">·</span>
                    <span className="whitespace-nowrap">
                      Starts {formatDateOnly(p.requested_start_date)}
                    </span>
                    <span className="ml-auto font-mono whitespace-nowrap">
                      {formatMoneyCents(p.delivery_install_budget_cents)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {!needsConfirmOnly && data.page.total > query.limit && (
            <div className="border-t border-line px-4 py-3 flex items-center justify-between text-sm">
              <div className="text-ink-faint">
                Page {Math.floor(query.offset / query.limit) + 1} of{' '}
                {Math.ceil(data.page.total / query.limit)}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={query.offset === 0}
                  onClick={() =>
                    setQuery({ ...query, offset: Math.max(0, query.offset - query.limit) })
                  }
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={query.offset + query.limit >= data.page.total}
                  onClick={() => setQuery({ ...query, offset: query.offset + query.limit })}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Deleted projects — supervisor/admin restore surface (collapsed by default). */}
      {canSeeRequests && <DeletedProjectsSection />}
    </div>
  );
}

/** Collapsible list of soft-deleted projects with a per-row Restore. */
function DeletedProjectsSection() {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useDeletedProjects(open);
  const rows = data?.data ?? [];

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium hover:bg-fill-soft transition-colors"
      >
        <Trash2 className="w-4 h-4 text-ink-faint" />
        Deleted projects
        <ChevronDown
          className={cn('w-4 h-4 text-ink-faint transition-transform ml-1', open && 'rotate-180')}
        />
        {open && rows.length > 0 && (
          <span className="ml-auto text-xs text-ink-faint">{rows.length}</span>
        )}
      </button>
      {open && (
        <ul className="divide-y divide-line border-t border-line">
          {isLoading && (
            <li className="px-4 py-6 text-center text-sm text-ink-dim">Loading…</li>
          )}
          {!isLoading && rows.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-ink-dim">No deleted projects.</li>
          )}
          {rows.map((p) => (
            <DeletedRow key={p.id} project={p} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function DeletedRow({ project }: { project: Project }) {
  const restore = useRestoreProject(project.id);
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <div className="font-medium truncate">{project.title}</div>
        <div className="text-xs text-ink-faint truncate">
          {project.client_name}
          {project.deleted_at && <> · deleted {formatDate(project.deleted_at)}</>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <StatusBadge status={project.status} />
        <Button
          variant="outline"
          size="sm"
          disabled={restore.isPending}
          onClick={() =>
            restore.mutate(undefined, {
              onSuccess: () => toast.success(`"${project.title}" restored`),
              onError: (e) => toast.error(e.message),
            })
          }
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Restore
        </Button>
      </div>
    </li>
  );
}

/** A clickable column header that drives server-side sort + shows direction. */
function SortHeader({
  field,
  label,
  align,
  query,
  onSort,
}: {
  field: ProjectSortField;
  label: string;
  align?: 'right';
  query: ProjectListQuery;
  onSort: (field: ProjectSortField) => void;
}) {
  const active = query.sort === field;
  return (
    <th className={cn('px-4 py-2 font-medium whitespace-nowrap', align === 'right' && 'text-right')}>
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-label={`Sort by ${label}`}
        className={cn(
          'inline-flex items-center gap-1 hover:text-ink transition-colors',
          active && 'text-ink',
        )}
      >
        {label}
        {active ? (
          query.order === 'asc' ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )
        ) : (
          <ChevronsUpDown className="w-3 h-3 opacity-40" />
        )}
      </button>
    </th>
  );
}

function StatusFilter({
  value,
  onChange,
}: {
  value: ProjectStatus | undefined;
  onChange: (s: ProjectStatus | undefined) => void;
}) {
  return (
    <div className="flex gap-1 flex-wrap">
      <FilterChip active={!value} onClick={() => onChange(undefined)}>
        All
      </FilterChip>
      {ProjectStatusSchema.options.map((s) => (
        <FilterChip key={s} active={value === s} onClick={() => onChange(s)}>
          <StatusBadge status={s} className="!px-1.5 !py-0" />
        </FilterChip>
      ))}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2 py-1 text-xs rounded-md border transition-colors',
        active
          ? 'border-ink bg-ink text-bg'
          : 'border-line bg-bg text-ink-dim hover:bg-fill-soft',
      )}
    >
      {children}
    </button>
  );
}

/** A small pill flagging open task requests (pending confirmation) on a row. */
function RequestsChip({ count }: { count: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-status-requested px-2 py-0.5 text-[11px] font-medium text-status-requested-fg"
      title={`${count} task ${count === 1 ? 'request' : 'requests'} awaiting confirmation`}
    >
      <ClipboardCheck className="w-3 h-3" />
      {count} {count === 1 ? 'request' : 'requests'}
    </span>
  );
}
